import { inject } from '@angular/core';
import { getSelfRegistration } from '@app/app.reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { UserAccountTariffSpecificLimits } from '@products/models/tariff-status.model';
import { TariffStatusService } from '@products/services/tariff-status.service';
import { ContingentBookingReservationActions } from '@store/products/booking/product/contingent/contingent-booking.actions';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import { ProductBookingActions } from '@store/products/booking/product/product-booking.actions';
import { getAllBookedTariffs } from '@store/products/booking/product/product-booking.selectors';
import { VoucherProductBookingActions } from '@store/products/booking/product/voucher/voucher-booking.actions';
import { BookingSmartPromotionActions } from '@store/products/booking/smart-promotion/booking-smart-promotion.actions';
import { ProductSelectionActions } from '@store/products/product-selection/product-selection.actions';
import { getAllTariffs } from '@store/products/product-selection/product-selection.selectors';
import {
  ContingentProductActions,
  PackageProductActions,
  SmartPromotionActions,
  TariffProductActions,
  VoucherProductActions
} from '@store/products/product.actions';
import { TariffStatusActions } from '@store/products/status/tariff/tariff-status.actions';
import { getAvailableTariffs } from '@store/products/status/tariff/tariff-status.selectors';
import { isEmpty, isNull } from 'lodash';
import { EMPTY, catchError, concatMap, filter, map, of, switchMap } from 'rxjs';

export const getTariffsUserAccountLimitStatus$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), tariffStatusService = inject(TariffStatusService)) =>
    actions$.pipe(
      ofType(TariffStatusActions.getUserAccountTariffsLimitStatus),
      filter(({ exhibitionId }) => !isNull(exhibitionId)),
      concatLatestFrom(() => store.select(getSelfRegistration)),
      switchMap(([{ exhibitionId }, isSelfRegistration]) =>
        tariffStatusService.getTicketLimitPerUserAccount$(exhibitionId, isSelfRegistration).pipe(
          map((userAccountTicketLimit: number) =>
            TariffStatusActions.setUserAccountTariffsLimitStatus(userAccountTicketLimit)
          ),
          catchError(() => of(TariffStatusActions.setUserAccountTariffsLimitStatus(null)))
        )
      )
    ),
  { functional: true }
);

export const getTariffSpecificUserAccountLimitsStatus$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), tariffStatusService = inject(TariffStatusService)) =>
    actions$.pipe(
      ofType(TariffStatusActions.getUserAccountTariffSpecificLimitsStatus),
      filter(({ exhibitionId }) => !isNull(exhibitionId)),
      concatLatestFrom(() => store.select(getSelfRegistration)),
      switchMap(([{ exhibitionId }, isSelfRegistration]) =>
        tariffStatusService.getAvailableTicketsPerTariffForUserAccount$(exhibitionId, isSelfRegistration).pipe(
          map((userAccountTariffSpecificLimits: UserAccountTariffSpecificLimits) =>
            TariffStatusActions.setUserAccountTariffSpecificLimitsStatus(userAccountTariffSpecificLimits)
          ),
          catchError(() => of(TariffStatusActions.setUserAccountTariffsLimitStatus(null)))
        )
      )
    ),
  { functional: true }
);

export const setInitialAvailableTariffsStatus$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), tariffStatusService = inject(TariffStatusService)) =>
    actions$.pipe(
      ofType(
        ProductSelectionActions.setInitialProductSelectionList,
        ProductSelectionActions.setPreferredProductSelectionList
      ),
      concatLatestFrom(() => store.select(getAllTariffs)),
      map(([_, tariffs]) => {
        const availableTariffs = tariffStatusService.mapTariffsToAvailableTariffs(tariffs);

        return TariffStatusActions.setAvailableTariffsStatus({ availableTariffs });
      })
    ),
  { functional: true }
);

export const setAvailableTariffsStatus$ = createEffect(
  (actions$ = inject(Actions), tariffStatusService = inject(TariffStatusService)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.setProductInBookingList,
        VoucherProductBookingActions.setVoucherProductInBookingList,
        PackageProductBookingActions.setPackageProductInBookingList,
        ProductBookingActions.removeProductFromBookingList,
        VoucherProductBookingActions.removeVoucherProductFromBookingList,
        PackageProductBookingActions.removePackageFromBookingList
      ),
      map(({ bookingProductTypes }) => {
        const availableTariffs = tariffStatusService.mapBookedProductTypesToAvailableTariffs(bookingProductTypes);

        return TariffStatusActions.setAvailableTariffsStatus({ availableTariffs });
      })
    ),
  { functional: true }
);

export const setAvailableTariffsStatusAfterAnonymousVoucherProductRedeem$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), tariffStatusService = inject(TariffStatusService)) =>
    actions$.pipe(
      ofType(VoucherProductActions.setRedeemedAndReleaseVouchersInProductBookingsAndProductSelection),
      filter(({ redeemedVoucher }) => redeemedVoucher && redeemedVoucher?.isAnonymous),
      concatLatestFrom(() => [store.select(getAllBookedTariffs), store.select(getAvailableTariffs)]),
      concatMap(([{ removeProductBookings: removedProductBookings }, bookedTariffs, currentAvailableTariffs]) => {
        if (isEmpty(removedProductBookings)) {
          return EMPTY;
        }

        const availableTariffs =
          tariffStatusService.mapRemovedBookingProductTypesToAvailableTariffsAfterAnonymousVoucherRedeem(
            removedProductBookings,
            bookedTariffs,
            currentAvailableTariffs
          );

        return of(TariffStatusActions.setAvailableTariffsStatus({ availableTariffs }));
      })
    ),
  { functional: true }
);

export const isTariffLoadingStatus$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        TariffProductActions.setTariffBooking,
        VoucherProductActions.setVoucherTariffBooking,
        PackageProductActions.setPackageBooking,
        PackageProductActions.setPackageTariffBooking,
        TariffProductActions.removeTariffBooking,
        VoucherProductActions.removeVoucherTariffBooking,
        PackageProductActions.removePackageBooking,
        PackageProductActions.removePackageTariffBooking,
        ProductBookingActions.setProductInBookingList,
        VoucherProductBookingActions.setVoucherProductInBookingList,
        PackageProductBookingActions.setPackageProductInBookingList,
        ProductBookingActions.removeProductFromBookingList,
        VoucherProductBookingActions.removeVoucherProductFromBookingList,
        PackageProductBookingActions.removePackageFromBookingList,
        BookingSmartPromotionActions.setBookedProductsPromoCodeDiscount,
        BookingSmartPromotionActions.removeBookedProductsPromoCodeDiscount,
        ProductSelectionActions.setPackageInSelectionList,
        ProductSelectionActions.removePackageFromSelectionList
      ),
      map(({ type }) => {
        const productActionTypes: string[] = [
          TariffProductActions.setTariffBooking.type,
          VoucherProductActions.setVoucherTariffBooking.type,
          PackageProductActions.setPackageBooking.type,
          PackageProductActions.setPackageTariffBooking.type,
          TariffProductActions.removeTariffBooking.type,
          VoucherProductActions.removeVoucherTariffBooking.type,
          PackageProductActions.removePackageBooking.type,
          PackageProductActions.removePackageTariffBooking.type
        ];

        const isTariffLoading = productActionTypes.includes(type);

        return TariffStatusActions.setIsTariffLoadingStatus(isTariffLoading);
      })
    ),
  { functional: true }
);

export const setCreateContingentReservationLoadingStatus$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        ContingentProductActions.setContingentBookingReservation,
        ContingentBookingReservationActions.setValidatedContingentReservationInBooking,
        ContingentProductActions.removeContingentBookingReservation
      ),
      map(({ type }) => {
        const isCreateContingentBookingReservationLoading =
          type === ContingentProductActions.setContingentBookingReservation.type ||
          type === ContingentProductActions.removeContingentBookingReservation.type;

        return TariffStatusActions.setIsCreateContingentReservationLoadingStatus(
          isCreateContingentBookingReservationLoading
        );
      })
    ),
  { functional: true }
);

export const setPromoCodeDiscountLoadingStatus$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(
        SmartPromotionActions.redeemPromoCode,
        SmartPromotionActions.removeRedeemedPromoCode,
        BookingSmartPromotionActions.setBookedProductsPromoCodeDiscount,
        BookingSmartPromotionActions.removeBookedProductsPromoCodeDiscount
      ),
      map(({ type }) => {
        const discountPromoCodeLoadingOnActionTypes: string[] = [
          SmartPromotionActions.redeemPromoCode.type,
          SmartPromotionActions.removeRedeemedPromoCode.type
        ];

        const isDiscountPromoCodeLoading = discountPromoCodeLoadingOnActionTypes.includes(type);

        return TariffStatusActions.setIsPromoCodeDiscountLoadingStatus(isDiscountPromoCodeLoading);
      })
    ),
  { functional: true }
);
